import {
  DocumentNode,
  ObservableQuery,
  OperationVariables,
  useQuery
} from "@apollo/client"
import { PW_AUTH_TOKEN_KEY } from "@pathwright/auth/src/api/client"
import type { TokenEventDetail } from "@pathwright/auth/src/lib/utils/auth-token"
import { useEffect } from "react"
import { usePathwrightContext } from "../pathwright/PathwrightContext"

// Skip a query if there is no user or there is no auth token.
// There may be cases where one exists and no the other, like
// during a Apollo store reset.
export const useSkipAuthRequiredQuery = () => {
  const { me } = usePathwrightContext()
  const authToken = localStorage.getItem("auth_token")
  return Boolean(!me || (!authToken && process.env.NODE_ENV !== "test"))
}

// `skip` only reaches the observable on the next render, but the store reset
// that follows sign-out refetches every non-standby query before that render.
// The token event fires synchronously ahead of the reset, so park the query in
// standby (what `skip` does) right away. The next render keeps it there.
export const useStandbyOnSignOut = (observable: ObservableQuery<any, any>) => {
  useEffect(() => {
    const handler = (event: Event) => {
      const { token } = (event as CustomEvent<TokenEventDetail>).detail
      if (!token && observable.options.fetchPolicy !== "standby") {
        observable.setOptions({ fetchPolicy: "standby" })
      }
    }
    window.addEventListener(PW_AUTH_TOKEN_KEY, handler)
    return () => window.removeEventListener(PW_AUTH_TOKEN_KEY, handler)
  }, [observable])
}

// handles refetching query when auth changes
const useAuthAwareQuery = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  operation: DocumentNode,
  options = {}
) => {
  const query = useQuery<TData, TVariables>(operation, {
    ...(options || {}),
    skip: useSkipAuthRequiredQuery()
  })
  useStandbyOnSignOut(query.observable)

  return query
}

export default useAuthAwareQuery
